import { useState } from 'react';
import { ArrowUp, Loader2, Check } from 'lucide-react';
import type { InputBox } from '../api/types';
import { api } from '../api/client';
import './SuggestionBar.css';

interface Props {
  sessionName: string;
  data: InputBox;
}

/**
 * Renders the live TUI input bar (draft text + autocomplete ghost) as
 * a clickable strip with a Send button. Mirrors what Claude Code's
 * terminal shows at the bottom of the pane:
 *
 *     > Neue front[autocomplete-suggestion]
 *
 * Frontend takeover: clicking Send pushes `text + suggestion` as a
 * full line into the agent's tmux pane, so the user doesn't have to
 * switch back to the terminal just to press Enter on a suggestion the
 * CLI is already offering.
 *
 * Hidden whenever both text and suggestion are empty (no draft).
 */
export function SuggestionBar({ sessionName, data }: Props) {
  const text = data.text || '';
  const suggestion = data.suggestion || '';
  // Guard against the "I clicked 3× because nothing happened, then F5 showed it
  // sent 3×" bug (Alex 2026-06-22): busy disables during the POST, sentText
  // disables re-send of the SAME draft until the pane's draft changes.
  const [busy, setBusy] = useState(false);
  const [sentText, setSentText] = useState<string | null>(null);
  if (!text && !suggestion) return null;
  const full = (text + suggestion).trim();
  const alreadySent = sentText === full;
  const send = async () => {
    if (!full || busy || alreadySent) return;
    setBusy(true);
    try {
      await api.sessions.sendInput(sessionName, full);
      setSentText(full);
    } catch (err) {
      console.error('send-suggestion failed', err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="sb-row" title="Im Pane gerade angezeigter Draft + Autocomplete">
      <div className="sb-marker">›</div>
      <div className="sb-content">
        {text && <span className="sb-text">{text}</span>}
        {suggestion && <span className="sb-ghost">{suggestion}</span>}
      </div>
      <button
        className={`sb-send${alreadySent ? ' sent' : ''}`}
        onClick={send}
        title={alreadySent ? 'Bereits gesendet' : `Sendet "${full}" als Zeile in die Pane`}
        disabled={!full || busy || alreadySent}
        aria-label="Send"
      >
        {busy ? <Loader2 size={14} className="sb-spin" /> : alreadySent ? <Check size={14} /> : <ArrowUp size={14} />}
      </button>
    </div>
  );
}
